import { Icon } from "@/components";
import { PulseLoader } from "@/components/pulse_loader";
import { usePriceBreakdownPerHourFeatureFlag } from "@/core/splitio";
import { formatCurrency } from "@/lib/currency/currency";
import { parseTime } from "@/lib/datetime";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { getSystemLocale } from "@/lib/locale/locale";
import { Time, TimeInterval } from "@/offering/time_utils";
import {
  DynamicPricingAdjustmentType,
  DynamicPricingBulkHour,
  useDynamicRatesQuery,
} from "@/offering_v2/hooks/use_dynamic_rates_query";
import {
  getDuration,
  toDurationString,
} from "@/offering_v2/utils/get_duration";
import classNames from "classnames";
import React, { useCallback } from "react";
import { Dropdown } from "react-bootstrap";
import { colors } from "../colors";
import { TimePicker, TimeSlot } from "./checkout_datetime_picker";
import styles from "./duration_range_picker.module.css";

interface DurationRangePickerProps {
  timeslots: TimeSlot[];
  value?: Partial<TimeInterval> | null;
  onChangeTime?: (value: Partial<TimeInterval>) => void;
  spaceId?: string;
  date?: string;
  unitPrice?: number;
  currency?: string;
}

export function DurationRangePicker({
  timeslots,
  value,
  onChangeTime,
  spaceId,
  date,
  unitPrice,
  currency,
}: DurationRangePickerProps) {
  const { start, end } = value || {};
  const { fbt } = useI18n();

  const priceBeakdownPerHourFeatureFlag = usePriceBreakdownPerHourFeatureFlag();

  const { data: dynamicRatesData } = useDynamicRatesQuery(
    spaceId,
    date,
    unitPrice
  );

  let lowestMinBookingUnit: number | undefined;
  dynamicRatesData?.options.forEach((o) => {
    if (o.type === "BulkHour") {
      if (!lowestMinBookingUnit || o.minBookingUnit < lowestMinBookingUnit) {
        lowestMinBookingUnit = o.minBookingUnit;
      }
    }
  });

  const duration = start && end ? getDuration(start, end) : undefined;
  const durationLabel = duration ? toDurationString(duration, fbt) : undefined;

  const handleStartTimeChange = useCallback(
    (time: Time) => {
      if (!onChangeTime) {
        return;
      }

      onChangeTime({
        start: time,
      });
    },
    [onChangeTime]
  );

  const handleDurationChange = useCallback(
    (endTime: Time) => {
      if (!onChangeTime) {
        return;
      }

      onChangeTime({
        start,
        end: endTime,
      });
    },
    [start, onChangeTime]
  );

  const firstTimeSlotPrice = timeslots[0]?.price;
  const timeslotsWithBestPrice = timeslots.map((s) => ({
    ...s,
    isBestRate: false,
  }));
  const hasDifferentPriceOnTimeslots = timeslots.some(
    (s) => s.price !== firstTimeSlotPrice
  );

  if (hasDifferentPriceOnTimeslots) {
    const bestValueStartTimePrice = timeslots.reduce(
      (value, cur) =>
        value && cur.price && value > cur.price ? cur.price : value,
      firstTimeSlotPrice
    );
    const foundBestOption = timeslotsWithBestPrice.find(
      (s) => s.price === bestValueStartTimePrice
    );
    if (foundBestOption) {
      foundBestOption.isBestRate = true;
    }
  }

  const startTimeOptions = timeslotsWithBestPrice.map((t) => ({
    value: t.startTime,
    label: fbt.formatTime(parseTime(t.startTime)),
    hasBetterRate: t.hasBetterRate,
    isBestRate: t.isBestRate,
    price:
      t.price && currency
        ? formatCurrency(t.price, currency, getSystemLocale())
        : undefined,
  }));
  const startTimeSlots = timeslots.find((t) => t.startTime === start);

  const durationOptions = startTimeSlots
    ? startTimeSlots.endTime.map((eT) => {
        const duration = getDuration(startTimeSlots.startTime, eT);
        let bulkHourDisount: DynamicPricingBulkHour | undefined;
        if (dynamicRatesData) {
          for (let i = 0; i < dynamicRatesData.options.length; i++) {
            const option = dynamicRatesData.options[i];
            if (
              option.type === "BulkHour" &&
              option.minBookingUnit <= duration
            ) {
              bulkHourDisount = option;
              dynamicRatesData.options.splice(i, 1);
            }
          }
        }
        const savingDisplay =
          bulkHourDisount?.adjustmentType ===
          DynamicPricingAdjustmentType.Percentage
            ? `${Math.abs(bulkHourDisount.adjustmentValue)}%`
            : bulkHourDisount?.adjustmentType ===
                DynamicPricingAdjustmentType.Amount && currency
            ? formatCurrency(
                Math.abs(bulkHourDisount.adjustmentValue),
                currency
              ) + fbt("/hour")
            : undefined;
        return {
          value: eT,
          label: toDurationString(duration, fbt),
          hasBulkDiscount: lowestMinBookingUnit
            ? duration >= lowestMinBookingUnit
            : false,
          bulkDiscountLabel:
            bulkHourDisount && !!savingDisplay
              ? fbt(
                  "Book " +
                    fbt.param("numberOfHours", bulkHourDisount.minBookingUnit) +
                    "+ hours, save " +
                    fbt.param("savingDisplay", savingDisplay)
                )
              : undefined,
        };
      })
    : [];

  return (
    <>
      <div className="time-range-picker-start" style={{ width: "48%" }}>
        {priceBeakdownPerHourFeatureFlag ? (
          <StartTimePicker
            placeholder={
              !startTimeOptions.length && !!date ? (
                <PulseLoader size={8} color={colors.blackMinus70} />
              ) : (
                fbt("Start time")
              )
            }
            onChange={handleStartTimeChange}
            options={startTimeOptions}
            value={start}
            label={start ? fbt.formatTime(parseTime(start)) : undefined}
            disabled={timeslots?.length < 1}
          />
        ) : (
          <TimePicker
            label={fbt("Start time")}
            onChange={handleStartTimeChange}
            options={startTimeOptions}
            value={start}
            disabled={timeslots?.length < 1}
          />
        )}
      </div>
      <div className="time-range-picker-end" style={{ width: "48%" }}>
        <DurationPicker
          placeholder={fbt("Duration")}
          onChange={handleDurationChange}
          options={durationOptions}
          value={end}
          label={durationLabel}
          disabled={!start}
        />
      </div>
    </>
  );
}

interface DurationPickerProps {
  placeholder: string;
  options: {
    label: string;
    value: string;
    hasBulkDiscount?: boolean;
    bulkDiscountLabel?: string;
  }[];
  value?: string;
  label?: string;
  onChange: (value: string) => void;
  disabled: boolean;
}

function DurationPicker({
  placeholder,
  value,
  label,
  options,
  onChange,
  disabled = false,
}: DurationPickerProps) {
  return (
    <div className="checkout-box-picker-view">
      <Dropdown
        className="checkout-box-picker"
        drop="down"
        onSelect={(value: string | null) => value && onChange(value)}
        align="end"
      >
        <Dropdown.Toggle
          disabled={disabled}
          className="w-100 d-flex align-items-center justify-content-between"
        >
          {label ? (
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {label}
            </div>
          ) : (
            <div>{placeholder}</div>
          )}
          <div>
            <Icon name="fi-chevron-down" size={24} />
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="time-picker-v2"
          style={{ minWidth: "100%", width: "fit-content" }}
        >
          {options.map((opt) => (
            <Dropdown.Item
              className={classNames(
                "time-picker-v2 text-nowrap",
                opt.hasBulkDiscount && styles.bulkDiscountDropdownItem,
                opt.bulkDiscountLabel && "mt-1"
              )}
              eventKey={opt.value}
              data-testid={"time-picker-v2-testId"}
              active={opt.value === value}
              style={{ width: opt.hasBulkDiscount ? 280 : undefined }}
              key={opt.value}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>{opt.label}</div>
                {opt.bulkDiscountLabel && (
                  <div
                    className="fw-bold rounded py-1 px-2"
                    style={{
                      color: "var(--bs-body-bg)",
                      fontSize: 12,
                      backgroundColor: "var(--dynamic-pricing)",
                    }}
                  >
                    {opt.bulkDiscountLabel}
                  </div>
                )}
              </div>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

interface StartTimePickerProps {
  placeholder: React.ReactNode;
  options: {
    label: string;
    value: string;
    price?: string;
    hasBetterRate?: boolean;
    isBestRate?: boolean;
  }[];
  value?: string;
  label?: string;
  onChange: (value: string) => void;
  disabled: boolean;
}

function StartTimePicker({
  placeholder,
  value,
  label,
  options,
  onChange,
  disabled,
}: StartTimePickerProps) {
  const { fbt } = useI18n();

  return (
    <div className="checkout-box-picker-view">
      <Dropdown
        className="checkout-box-picker"
        drop="down"
        onSelect={(value: string | null) => value && onChange(value)}
        align="end"
      >
        <Dropdown.Toggle
          disabled={disabled}
          className="w-100 d-flex align-items-center justify-content-between"
        >
          {label ? (
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {label}
            </div>
          ) : (
            <div>{placeholder}</div>
          )}
          <div>
            <Icon name="fi-chevron-down" size={24} />
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="time-picker-v2"
          style={{ minWidth: "100%", width: "fit-content" }}
        >
          {options.map((opt) => (
            <Dropdown.Item
              className={classNames("time-picker-v2", styles.dropdownItem)}
              eventKey={opt.value}
              data-testid={"time-picker-v2-testId"}
              active={opt.value === value}
              style={{ width: opt.isBestRate ? 280 : undefined }}
              key={opt.value}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>{opt.label}</div>
                {opt.price && (
                  <div className="d-flex justify-content-between align-items-center">
                    {opt.isBestRate && (
                      <div
                        className="fw-bold rounded text-white py-1 px-2 me-2 text-uppercase"
                        style={{
                          backgroundColor: colors.spinachCore,
                          fontSize: 12,
                        }}
                      >
                        {fbt("Best value")}
                      </div>
                    )}
                    <div
                      style={{
                        color:
                          opt.value === value
                            ? "white"
                            : opt.hasBetterRate
                            ? colors.spinachCore
                            : undefined,
                      }}
                    >
                      {opt.price}
                    </div>
                  </div>
                )}
              </div>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
